// src/hooks/admin/usePropertyEditor.ts
import { useState, useEffect } from 'react';
import { getAdminPropertyDetail, patchAdminProperty } from '@/API/apiService';

/**
 * フォームで扱うデータ構造
 */
interface FormData {
  is_published: boolean;
  name: string;
  name_en: string;
  address: string;
  address_en: string;
  station_distance: string;
  station_distance_en: string;
  area_id: string;
  area_name: string;
  area_name_en: string;
  description: string;
  description_en: string;
  site_controller_id: string;
  site_controller_property_id: string;
  region_id: string;
  latitude: string;
  longitude: string;
  postal_code: string;
  postal_code_en: string;
  room_type_description: string;
  room_type_description_en: string;
  sort_number: string;
}

/**
 * 画像データの型定義
 * - 既存画像は id と url を持つ
 * - 新規追加画像は file と url を持つ
 */
type UploadedImage = { id: number; url: string } | { file: File; url: string };

/**
 * フックが返す型
 */
interface UsePropertyEditor {
  formData: FormData;
  images: UploadedImage[];
  loading: boolean;
  handleInputChange: (field: keyof FormData, value: string | boolean) => void;
  handleAddImages: (newImages: File[]) => void;
  handleRemoveImage: (index: number) => void;
  handleSaveAll: () => Promise<void>;
}

export const usePropertyEditor = (propertyId?: string): UsePropertyEditor => {
  const [formData, setFormData] = useState<FormData>({
    is_published: false,
    name: '',
    name_en: '',
    address: '',
    address_en: '',
    station_distance: '',
    station_distance_en: '',
    area_id: '',
    area_name: '',
    area_name_en: '',
    description: '',
    description_en: '',
    site_controller_id: '',
    site_controller_property_id: '',
    region_id: '1',
    latitude: '',
    longitude: '',
    postal_code: '',
    postal_code_en: '',
    room_type_description: '',
    room_type_description_en: '',
    sort_number: '',
  });
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [deleteImageIds, setDeleteImageIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!propertyId) {
        setLoading(false);
        return;
      }
      try {
        const res = await getAdminPropertyDetail(propertyId);
        const data = res.data.data;

        setFormData({
          is_published: !!data.is_published,
          name: data.name || '',
          name_en: data.name_en || '',
          address: data.address || '',
          address_en: data.address_en || '',
          station_distance: data.station_distance || '',
          station_distance_en: data.station_distance_en || '',
          area_id: data.area_id != null ? data.area_id.toString() : '',
          area_name: data.area_name || '',
          area_name_en: data.area_name_en || '',
          description: data.description || '',
          description_en: data.description_en || '',
          site_controller_id:
            data.site_controller_id != null
              ? data.site_controller_id.toString()
              : '',
          site_controller_property_id: data.site_controller_property_id || '',
          region_id: data.region_id != null ? data.region_id.toString() : '1',
          latitude: data.latitude || '',
          longitude: data.longitude || '',
          postal_code: data.postal_code || '',
          postal_code_en: data.postal_code_en || '',
          room_type_description: data.room_type_description || '',
          room_type_description_en: data.room_type_description_en || '',
          sort_number:
            data.sort_number != null ? data.sort_number.toString() : '',
        });

        setImages(
          Array.isArray(data.images)
            ? data.images.map((img: any) => ({ id: img.id, url: img.url }))
            : []
        );
        setFilesToUpload([]);
        setDeleteImageIds([]);
      } catch (e) {
        console.error('物件情報の取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [propertyId]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddImages = (newImages: File[]) => {
    const objs = newImages.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...objs]);
    setFilesToUpload((prev) => [...prev, ...newImages]);
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => {
      const img = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if ('id' in img) {
        setDeleteImageIds((ids) => [...ids, img.id]);
      } else {
        setFilesToUpload((files) => files.filter((f) => f !== img.file));
      }
      return next;
    });
  };

  const handleSaveAll = async () => {
    if (!propertyId) throw new Error('propertyId が指定されていません。');

    const fd = new FormData();
    fd.append('is_published', formData.is_published ? 'true' : 'false');
    fd.append('name', formData.name);
    fd.append('name_en', formData.name_en);
    fd.append('address', formData.address);
    fd.append('address_en', formData.address_en);
    fd.append('station_distance', formData.station_distance);
    fd.append('station_distance_en', formData.station_distance_en);
    fd.append(
      'area_id',
      formData.area_id ? parseInt(formData.area_id).toString() : '0'
    );
    fd.append('area_name', formData.area_name);
    fd.append('area_name_en', formData.area_name_en);
    fd.append('description', formData.description);
    fd.append('description_en', formData.description_en);
    fd.append(
      'site_controller_id',
      formData.site_controller_id
        ? parseInt(formData.site_controller_id).toString()
        : '0'
    );
    fd.append(
      'site_controller_property_id',
      formData.site_controller_property_id
    );
    fd.append(
      'region_id',
      formData.region_id ? parseInt(formData.region_id).toString() : '1'
    );
    fd.append(
      'latitude',
      formData.latitude ? parseFloat(formData.latitude).toString() : ''
    );
    fd.append(
      'longitude',
      formData.longitude ? parseFloat(formData.longitude).toString() : ''
    );
    fd.append('postal_code', formData.postal_code);
    fd.append('postal_code_en', formData.postal_code_en);
    fd.append('room_type_description', formData.room_type_description);
    fd.append('room_type_description_en', formData.room_type_description_en);
    fd.append(
      'sort_number',
      formData.sort_number ? parseInt(formData.sort_number).toString() : '0'
    );

    filesToUpload.forEach((file) => {
      fd.append('add_images[]', file);
    });

    deleteImageIds.forEach((id) => {
      fd.append('delete_images[]', id.toString());
    });

    try {
      await patchAdminProperty(propertyId, fd);
      alert('物件更新成功しました。');
    } catch (e) {
      console.error('物件更新エラー:', e);
      alert('物件更新エラーが発生しました。');
    }
  };

  return {
    formData,
    images,
    loading,
    handleInputChange,
    handleAddImages,
    handleRemoveImage,
    handleSaveAll,
  };
};

export default usePropertyEditor;
